<script lang="ts">
  // History timeline per INTERFACE-SPECIFICATION.md §9.1.
  //
  // For a given product, lists every manifest version with its
  // effective-from, status, and approver. Clicking any version
  // opens the point-in-time reconstruction (HistorySnapshot).
  // Selecting two versions opens the delta view.

  import type { Jurisdiction, ProductId, ManifestVersionEntry } from '../lib/types';
  import { getAllVersions, catalogueState } from '../lib/catalogue-store.svelte';
  import { formatLocalDate, formatEffectiveTimestamp, formatClampedFromNote } from '../lib/formatters';

  interface Props {
    jurisdiction: Jurisdiction;
    product: ProductId;
    selected: Set<string>;
    onToggle: (gitHash: string) => void;
  }

  const { jurisdiction, product, selected, onToggle }: Props = $props();

  // History is bounded per-product but can grow long on a busy product.
  // Cap the display to the 30 most-recent entries (by effective_from
  // descending — the audit-log reading order); when the cap kicks in,
  // a muted footer names the total count so the operator knows older
  // entries exist. Future work could add proper pagination; PR #102
  // shipped the same shape for Recently-completed.
  const HISTORY_DISPLAY_CAP = 30;
  const sortedVersions = $derived(
    [...getAllVersions(catalogueState.manifest, jurisdiction, product)].sort((a, b) => {
      const aMs = new Date(a.effective_from.utc).getTime();
      const bMs = new Date(b.effective_from.utc).getTime();
      return bMs - aMs;
    })
  );
  const versions = $derived(sortedVersions.slice(0, HISTORY_DISPLAY_CAP));
  const overflowCount = $derived(Math.max(0, sortedVersions.length - HISTORY_DISPLAY_CAP));

  function indicator(v: ManifestVersionEntry): string {
    if (v.status === 'in_effect') return '●';
    if (v.status === 'approved') return '●';
    if (v.status === 'dequeued') return '⊘';
    return '○';
  }
  function statusLabel(v: ManifestVersionEntry): string {
    switch (v.status) {
      case 'in_effect': return 'In Effect';
      case 'approved': return 'Approved · queued';
      case 'superseded': return 'Superseded';
      case 'under_review': return 'Under Review';
      case 'proposed': return 'Proposed';
      case 'dequeued': return 'Dequeued — cancelled before live';
      default: return v.status;
    }
  }
</script>

<div class="table-scroll">
<table class="cube-table">
  <thead>
    <tr>
      <th></th>
      <th>Version</th>
      <th>Effective</th>
      <th>Status</th>
      <th>Approved by</th>
      <th>Hash</th>
    </tr>
  </thead>
  <tbody>
    {#each versions as v (v.catalogue_version)}
      <tr>
        <td>
          <input
            type="checkbox"
            checked={selected.has(v.git_hash)}
            onchange={() => onToggle(v.git_hash)}
            aria-label="Select version {v.catalogue_version}"
          />
        </td>
        <td>
          <span class="indicator" data-state={v.status}>{indicator(v)}</span>
          <code>{v.catalogue_version}</code>
        </td>
        <td>
          {formatEffectiveTimestamp(v.effective_from)}
          {#if v.proposed_effective_from}
            <div class="clamp-note muted">{formatClampedFromNote(v.proposed_effective_from)}</div>
          {/if}
        </td>
        <td class="muted">
          {statusLabel(v)}
          {#if v.status === 'dequeued' && (v.dequeued_by || v.dequeued_reason)}
            <div class="dequeue-note">
              {#if v.dequeued_by}Dequeued by {v.dequeued_by}{/if}
              {#if v.dequeued_reason}
                <span class="dequeue-reason">· {v.dequeued_reason}</span>
              {/if}
            </div>
          {/if}
        </td>
        <td class="muted">{v.approved_by ?? '—'}</td>
        <td class="muted"><code>{v.git_hash.slice(0, 8)}</code></td>
      </tr>
    {/each}
  </tbody>
</table>
{#if overflowCount > 0}
  <p class="history-overflow muted">
    Showing the {HISTORY_DISPLAY_CAP} most recent of {sortedVersions.length} entries. Older entries are available via the catalogue's git history.
  </p>
{/if}
</div>

<style>
  .indicator[data-state="in_effect"] { color: var(--accent-pass); margin-right: var(--space-1); }
  .indicator[data-state="approved"] { color: var(--accent-info); margin-right: var(--space-1); }
  .indicator[data-state="superseded"] { color: var(--surface-faint); margin-right: var(--space-1); }
  .indicator[data-state="dequeued"] { color: var(--color-warn, #b45309); margin-right: var(--space-1); }
  .dequeue-note {
    margin-top: var(--space-1);
    font-size: var(--type-xs, 0.75rem);
    color: var(--surface-muted);
  }
  .dequeue-reason {
    font-style: italic;
  }
  code { font-family: var(--font-mono); }
  .history-overflow {
    margin: var(--space-3) 0 0 0;
    font-size: var(--type-sm, 0.875rem);
  }
</style>
